// Função para criar uma imagem a partir de um blob e retornar como File
export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Necessário para imagens de outras origens
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Retorna o url da imagem cortada a partir do src da imagem e os pixels da área de corte.
 * @param {string} imageSrc - URL da imagem.
 * @param {Object} pixelCrop - Objeto com x, y, width, height da área de corte em pixels.
 * @param {number} rotation - Rotação da imagem em graus.
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  rotation = 0
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas 2D context');
  }

  const rotRad = getRadianAngle(rotation);

  // Calcula o novo tamanho do canvas para acomodar a rotação
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotRad
  );

  // Define o tamanho do canvas para o tamanho rotacionado
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Move o ponto de origem para o centro do canvas para rotação
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(1, 1); // Garante que a imagem não seja espelhada
  ctx.translate(-image.width / 2, -image.height / 2);

  // Desenha a imagem rotacionada
  ctx.drawImage(image, 0, 0);

  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  // Crie um novo canvas para a imagem cortada
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    throw new Error('Could not get cropped canvas 2D context');
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.putImageData(data, 0, 0);

  // Retorna a imagem cortada como um Blob e depois como File
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((blob) => {
      if (!blob) {
        console.error('Canvas is empty');
        reject(new Error('Canvas is empty'));
        return;
      }
      const fileName = `cropped_image_${Date.now()}.jpeg`; // Nome do arquivo
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      resolve(file);
    }, 'image/jpeg', 0.95); // Qualidade da imagem (0 a 1)
  });
}

function rotateSize(width: number, height: number, rotation: number) {
  const absRotation = Math.abs(rotation);
  const sin = Math.sin(absRotation);
  const cos = Math.cos(absRotation);
  const newWidth = width * cos + height * sin;
  const newHeight = height * cos + width * sin;
  return { width: newWidth, height: newHeight };
}
